// src/components/ChatAssistant.js
import React, { useState, useEffect, useRef } from 'react';
import { Bot, X, Bookmark, Send } from 'lucide-react';
import { api, API_BASE } from '../api/db';

export default function ChatAssistant({ currentUser, portfolio, themeClasses, setIsChatPanelOpen, notes, setNotes }) {
  const [chatInput, setChatInput] = useState("");
  const [isChatLoading, setIsChatLoading] = useState(false);
  const scrollRef = useRef(null);
  const [messages, setMessages] = useState([]);

  useEffect(() => {
    api.getUserData(currentUser.id).then(data => {
      setMessages(data.chat || [{"role":"ai","text":"Analyst Online."}]);
    });
  }, [currentUser]);

  useEffect(() => {
    if (messages.length > 0 && !isChatLoading) {
      api.syncUserData(currentUser.id, { chat: messages });
    }
  }, [messages, isChatLoading, currentUser]);

  const askGemini = async () => {
    if (!chatInput.trim() || isChatLoading) return;
    const userMsg = { role: 'user', text: chatInput };
    setMessages(prev => [...prev, userMsg]);
    setChatInput("");
    setIsChatLoading(true);
    
    try {
      const prompt = `Context: Portfolio ${JSON.stringify(portfolio)}. Query: ${userMsg.text} Format using markdown bolding and bullets.`;
      
      // We now call your secure Python backend proxy, NOT Google directly!
      const response = await fetch(`${API_BASE}/gemini/chat`, {
        method: 'POST', 
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ contents: [{ parts: [{ text: prompt }] }] })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        console.error("Gemini API Error:", data);
        throw new Error(data.error?.message || "API request failed");
      }

      const rawAiText = data.candidates[0].content.parts[0].text;
      
      setMessages(prev => [...prev, { role: 'ai', text: '' }]);
      let currentText = ''; let charIdx = 0;
      
      const streamInterval = setInterval(() => {
          currentText += rawAiText.substring(charIdx, charIdx + 5);
          charIdx += 5;
          setMessages(prev => {
              const newMsgs = [...prev];
              newMsgs[newMsgs.length - 1] = { role: 'ai', text: currentText };
              return newMsgs;
          });
          if (charIdx >= rawAiText.length) {
              clearInterval(streamInterval);
              setMessages(prev => {
                  const newMsgs = [...prev];
                  newMsgs[newMsgs.length - 1] = { role: 'ai', text: rawAiText };
                  return newMsgs;
              });
              setIsChatLoading(false);
          }
      }, 10);
    } catch (e) {
      console.error("Chatbot caught an error: ", e);
      setMessages(prev => [...prev, { role: 'ai', text: `ERROR: ${e.message || "API Unavailable."}` }]);
      setIsChatLoading(false);
    }
  };

  useEffect(() => { scrollRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages, isChatLoading]);

  const formatMarkdownText = (text) => {
    return text.split('\n').map((line, idx) => {
      const isBullet = line.trim().startsWith('* ') || line.trim().startsWith('- ');
      const content = isBullet ? line.trim().substring(2) : line;
      const elements = content.split(/(\*\*.*?\*\*)/g).map((part, i) => 
        part.startsWith('**') ? <strong key={i} className={`font-black ${themeClasses.textHead}`}>{part.slice(2, -2)}</strong> : <span key={i}>{part}</span>
      );
      if (isBullet) return <li key={idx} className="ml-4 list-disc mb-1">{elements}</li>;
      if (line.trim() === '') return <br key={idx} />;
      return <p key={idx} className="mb-2">{elements}</p>;
    });
  };

  return (
    <div className="flex flex-col h-full">
      <div className={`p-4 border-b ${themeClasses.border} flex justify-between items-center shrink-0`}>
        <span className="text-xs font-black uppercase text-indigo-500 flex items-center gap-2 tracking-widest"><Bot size={16}/> Gemini Assistant</span>
        <button onClick={() => setIsChatPanelOpen(false)} className={`${themeClasses.textMuted} hover:text-red-500 transition-colors`}><X size={16}/></button>
      </div>
      <div className="flex-1 overflow-y-auto p-4 space-y-4 text-xs custom-scrollbar">
        {messages.map((m, i) => (
          <div key={i} className={`flex flex-col ${m.role === 'user' ? 'items-end' : 'items-start'}`}>
            <div className={`p-3 rounded-2xl max-w-[90%] leading-relaxed relative group ${m.role === 'user' ? 'bg-indigo-600 text-white rounded-tr-sm' : `${themeClasses.input} border ${themeClasses.border} rounded-tl-sm`}`}>
              {m.role === 'ai' && (
                <button onClick={() => setNotes([...notes, m.text])} className={`absolute -right-2 -top-2 p-1.5 opacity-0 group-hover:opacity-100 ${themeClasses.bgCard} rounded-full border ${themeClasses.border} text-indigo-500 shadow-md`} title="Clip Note"><Bookmark size={12}/></button>
              )}
              {formatMarkdownText(m.text)}
            </div>
          </div>
        ))}
        {isChatLoading && <div className={`text-[10px] ${themeClasses.textMuted} font-bold uppercase tracking-widest animate-pulse`}>Analyst is typing...</div>}
        <div ref={scrollRef} />
      </div>
      <div className={`p-4 border-t ${themeClasses.border} shrink-0`}>
        <div className="flex gap-2">
          <input value={chatInput} onChange={e => setChatInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && askGemini()} placeholder="Ask analyst..." disabled={isChatLoading} className={`flex-1 ${themeClasses.input} ${themeClasses.textHead} border ${themeClasses.border} rounded-xl p-3 text-xs outline-none focus:border-indigo-500`} />
          <button onClick={askGemini} disabled={isChatLoading} className="bg-indigo-600 text-white p-3 rounded-xl hover:bg-indigo-500 transition-colors disabled:opacity-50"><Send size={16}/></button>
        </div>
      </div>
    </div>
  );
}